/**
 * The areas, as a table on desktop and as cards on a phone (RESP2c).
 *
 * ONE component for both, because they are the same list with the same
 * selection and the same actions — two components would be two places to
 * forget that a row is selectable by keyboard.
 *
 * Selection never navigates. Clicking a row updates the panel beside it;
 * the kebab stops propagation so opening the menu does not also change
 * what the panel is showing, which is the kind of thing that makes an
 * interface feel like it is arguing with you.
 *
 * Selection is a real `<button>` in the first cell, not a `role="button"`
 * on the `<tr>`. A row that claims to be a button stops being a row: the
 * explicit role replaces the implicit one, and a table whose rows are
 * not rows is unnavigable with the table commands a screen-reader user
 * would otherwise use. The `<tr>` still has a click handler, but only as
 * a convenience for the mouse — everything it does is reachable from the
 * button, which is what carries the keyboard focus and the pressed
 * state.
 *
 * **There is no description column.** The shell's sidebar leaves this
 * table roughly 750px on a 1440 laptop, and a two-line clamp of prose
 * eats the width that Typ, Status and Aktionen need — the columns a
 * person actually acts on end up behind a horizontal scrollbar, which is
 * the worst place to put the controls. The description is in the detail
 * panel beside the table and on the phone card, both of which have the
 * room for it. A conditional column would have been the other option and
 * was rejected: a table whose shape changes with the window is a table
 * nobody can describe to a colleague.
 *
 * Which layout renders is decided in JavaScript by `useIsMobile` — the
 * same 768px breakpoint the rest of the shell uses — rather than by a
 * `hidden md:block` / `md:hidden` pair. Both would be correct in a
 * browser, because Tailwind's `hidden` is `display:none` and that does
 * remove a subtree from the accessibility tree. The JS version is
 * chosen because it keeps exactly ONE copy of every area in the
 * document: with the CSS version, anything walking the DOM without a
 * stylesheet — a test, a crawler, a text extractor — sees all thirteen
 * areas twice and cannot tell which copy is the real one.
 */
import React from "react";
import { MoreHorizontal, UserRound } from "lucide-react";

import { useIsMobile } from "../../../hooks/useIsMobile";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Skeleton } from "../../../components/ui/skeleton";

import {
  buildAreaPeople,
  overflowLabel,
  personInitials,
} from "./areaPeople";
import {
  areaColorToken,
  areaIcon,
  assignmentKind,
  assignmentKindLabel,
} from "./areaPresentation";

const HEAD =
  "px-2.5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground";

/** Active vs inactive, said in words as well as in colour. */
export function StatusBadge({ active }) {
  return (
    <span
      className={
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold " +
        (active
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
          : "border-border bg-muted text-muted-foreground")
      }
    >
      {active ? "Aktiv" : "Inaktiv"}
    </span>
  );
}

/** Person / Team / Offen — a presentation type, never an entity. */
export function TypeBadge({ kind }) {
  return (
    <span
      className={
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold " +
        (kind === "open"
          ? "border-border bg-muted text-muted-foreground"
          : "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300")
      }
    >
      {assignmentKindLabel(kind)}
    </span>
  );
}

/** The coloured tile that carries the area's icon. */
export function AreaIconTile({ area, size = "md" }) {
  const Icon = areaIcon(area?.icon);
  const box = size === "lg" ? "h-12 w-12 rounded-[14px]" : "h-9 w-9 rounded-[10px]";
  const glyph = size === "lg" ? "h-6 w-6" : "h-[18px] w-[18px]";
  return (
    <span
      aria-hidden="true"
      className={
        "flex shrink-0 items-center justify-center border " +
        box +
        " " +
        areaColorToken(area?.color_token)
      }
    >
      <Icon className={glyph} strokeWidth={1.75} />
    </span>
  );
}

/**
 * Who is suggested for this area — by name.
 *
 * Names, never a team object: Vantio has no team model and this does not
 * pretend otherwise. Several people named together describe a
 * configuration, not a group that exists somewhere.
 */
export function AssignmentCell({ area }) {
  const { visible, overflowCount, kind } = buildAreaPeople(area);

  if (kind === "open") {
    return (
      <span className="inline-flex items-center gap-2 text-muted-foreground">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-border"
        >
          <UserRound className="h-3.5 w-3.5" />
        </span>
        {/* Kept on one line: wrapped, it makes every unassigned row taller
            than every assigned one, and eleven of thirteen rows are
            unassigned in a fresh organisation. */}
        <span className="whitespace-nowrap text-[13px]">Nicht zugewiesen</span>
      </span>
    );
  }

  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground"
      >
        {personInitials(visible[0])}
      </span>
      {/* RESP2c.1: the NAMES, not a count. "2 Mitglieder" answered a
          question nobody asked — who is responsible is the point of the
          page. Two rows fit; beyond that the rest are counted, because a
          table cell is not a list. */}
      <span className="min-w-0">
        {visible.map((person) => (
          <span
            key={person.id || person.label}
            title={person.label}
            className={
              "block truncate text-[13px] " +
              (person.isLead ? "font-medium " : "") +
              (person.isActive
                ? "text-foreground"
                : "text-muted-foreground line-through")
            }
          >
            {person.label}
          </span>
        ))}
        {overflowCount ? (
          <span className="block text-[11px] text-muted-foreground">
            {overflowLabel(overflowCount)}
          </span>
        ) : null}
      </span>
    </span>
  );
}

/** The kebab. Identical on both layouts, so the rules live in one place. */
function RowActions({ area, canEdit, onEdit, onToggleActive, onDelete }) {
  if (!canEdit) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für ${area.label}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => onEdit(area)}>
          Bearbeiten
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onToggleActive(area)}>
          {area.is_active ? "Deaktivieren" : "Aktivieren"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {area.is_system ? (
          // Present but inert: hiding it entirely would leave a person
          // wondering where the action went, and the tooltip is the
          // answer to the question they would otherwise ask.
          <DropdownMenuItem
            disabled
            title="Systembereiche können nicht gelöscht werden."
          >
            Löschen
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => onDelete(area)}
            className="text-destructive focus:text-destructive"
          >
            Löschen
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SkeletonRows({ cols }) {
  return Array.from({ length: 6 }).map((_, i) => (
    <tr key={i} className="border-t border-border">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-4 py-3">
          <Skeleton className="h-4 w-full max-w-[160px]" />
        </td>
      ))}
    </tr>
  ));
}

export default function ResponsibilityAreaList({
  areas,
  loading,
  selectedId,
  onSelect,
  canEdit,
  onEdit,
  onToggleActive,
  onDelete,
  onAdd,
}) {
  const list = Array.isArray(areas) ? areas : [];
  const isMobile = useIsMobile();

  const rowClass = (area) =>
    "cursor-pointer border-t border-border transition-colors " +
    (area.id === selectedId
      ? "bg-violet-500/5 dark:bg-violet-500/10"
      : "hover:bg-muted/40");

  const activate = (area) => () => onSelect(area.id);

  /** The label button — the keyboard path to selecting an area. */
  const SelectButton = ({ area, size = "md" }) => (
    <button
      type="button"
      aria-pressed={area.id === selectedId}
      onClick={activate(area)}
      className="flex min-w-0 items-center gap-3 rounded-[10px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <AreaIconTile area={area} />
      {/* The cap is on the SPAN, not on the <td>: `max-width` on a table
          cell is ignored under the default `table-layout: auto`, so a long
          name would keep widening the column and overlap the next one.
          Tighter below `xl`, where the shell's sidebar leaves the table
          about 620px and the actions column is what gets pushed out. */}
      <span
        className={
          size === "lg" ? "min-w-0" : "min-w-0 max-w-[150px] xl:max-w-[220px]"
        }
      >
        <span
          title={area.label}
          className={
            "block truncate font-semibold text-foreground " +
            (size === "lg" ? "text-[14px]" : "text-[13px]")
          }
        >
          {area.label}
        </span>
        {!area.is_system ? (
          <span className="block text-[11px] text-muted-foreground">
            Eigener Bereich
          </span>
        ) : null}
      </span>
    </button>
  );

  if (!loading && list.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <p className="m-0 text-[15px] font-semibold text-foreground">
          Noch keine Tätigkeitsbereiche
        </p>
        <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-muted-foreground">
          Legen Sie fest, in welchen Bereichen Ihre Organisation arbeitet, um
          Aufgaben und Arbeitsrapporte zuordnen zu können.
        </p>
        {canEdit ? (
          <button
            type="button"
            onClick={onAdd}
            className="mt-4 inline-flex items-center gap-2 rounded-[10px] bg-violet-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Bereich hinzufügen
          </button>
        ) : null}
      </div>
    );
  }

  // ── Phone: cards, because a six-column table is not a phone UI ──
  if (isMobile) {
    return (
      <ul className="m-0 list-none divide-y divide-border p-0">
        {loading && list.length === 0
          ? Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="px-4 py-4">
                <Skeleton className="h-10 w-full" />
              </li>
            ))
          : list.map((area) => (
              <li
                key={area.id}
                className={
                  "px-4 py-4 " +
                  (area.id === selectedId
                    ? "bg-violet-500/5 dark:bg-violet-500/10"
                    : "")
                }
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <SelectButton area={area} size="lg" />
                    <p className="m-0 mt-1 line-clamp-2 text-[12px] text-muted-foreground">
                      {area.description || "—"}
                    </p>
                    <div className="mt-2">
                      <AssignmentCell area={area} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <TypeBadge kind={assignmentKind(area)} />
                      <StatusBadge active={area.is_active} />
                    </div>
                  </div>
                  <div className="shrink-0">
                    <RowActions
                      area={area}
                      canEdit={canEdit}
                      onEdit={onEdit}
                      onToggleActive={onToggleActive}
                      onDelete={onDelete}
                    />
                  </div>
                </div>
              </li>
            ))}
      </ul>
    );
  }

  // ── Tablet landscape and up: a real table ───────────────────────────
  return (
    <table className="w-full border-collapse">
      <caption className="sr-only">
        Tätigkeitsbereiche mit Standardverantwortlichen, Typ und Status
      </caption>
      <thead>
        <tr>
          <th scope="col" className={HEAD}>Bereich</th>
          {/* "Standardverantwortlicher" is a single 24-character word, so
              it cannot wrap and sets this column's minimum width all by
              itself — enough to push Aktionen off the right edge on a
              1440 laptop. The panel beside the table uses the full word
              where there is room for it. */}
          <th scope="col" className={HEAD}>Verantwortlich</th>
          <th scope="col" className={HEAD}>Typ</th>
          <th scope="col" className={HEAD}>Status</th>
          {/* Named for screen readers, blank on screen: the word is wider
              than the 32px button beneath it and was the last thing
              pushing the column off a 1024 tablet. */}
          <th scope="col" className={HEAD}>
            <span className="sr-only">Aktionen</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {loading && list.length === 0 ? (
          <SkeletonRows cols={6} />
        ) : (
          list.map((area) => (
            <tr
              key={area.id}
              onClick={activate(area)}
              className={rowClass(area)}
            >
              <td className="px-2.5 py-3">
                <SelectButton area={area} />
              </td>
              <td className="px-2.5 py-3">
                <AssignmentCell area={area} />
              </td>
              <td className="px-2.5 py-3">
                <TypeBadge kind={assignmentKind(area)} />
              </td>
              <td className="px-2.5 py-3">
                <StatusBadge active={area.is_active} />
              </td>
              <td className="px-2.5 py-3">
                <RowActions
                  area={area}
                  canEdit={canEdit}
                  onEdit={onEdit}
                  onToggleActive={onToggleActive}
                  onDelete={onDelete}
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
